import { ReactNode } from "react";
import clsx from "clsx";
import { Tooltip } from "@mui/material";
import { Icon } from "components/Icon";

type TProps = {
  /** Band title, e.g. "همه کنتورهای بازه". */
  title: string;
  /** Short scope chip, e.g. "۱۰ کنتور برتر". */
  badge?: string;
  /** Muted one-liner explaining what the band's figures cover. */
  description?: string;
  /** Longer explanation shown behind an info icon. */
  hint?: string;
  /** `accent` for the narrowed/top-N scope, `neutral` for full-population figures. */
  tone?: "neutral" | "accent";
  /** Control belonging to this band — e.g. the top-N slice selector. */
  action?: ReactNode;
  className?: string;
};

/**
 * Scope separator for the report dashboards.
 *
 * The overview page mixes two very different populations — figures over every
 * meter in the filtered region, and figures over just the top-N consumers. This
 * band marks where one ends and the other begins: a rule, a title, and a chip
 * naming the scope, so a reader never has to guess which set a number covers.
 */
export function ReportBand({
  title,
  badge,
  description,
  hint,
  tone = "neutral",
  action,
  className,
}: TProps) {
  return (
    <div className={clsx("flex items-center gap-3 pt-2", className)}>
      <span
        className={clsx(
          "h-4 w-1 shrink-0 rounded-full",
          tone === "accent" ? "bg-secondary-70" : "bg-primary-70"
        )}
      />
      <h3 className="shrink-0 text-sm font-bold text-primary-90">{title}</h3>

      {badge && (
        <span
          className={clsx(
            "shrink-0 rounded-full px-2 py-0.5 text-tiny font-medium",
            tone === "accent"
              ? "bg-secondary-20 text-secondary-100"
              : "bg-primary-20 text-primary-90"
          )}
        >
          {badge}
        </span>
      )}

      {description && (
        <span className="truncate text-tiny text-gray-90">{description}</span>
      )}

      {hint && (
        <Tooltip title={hint} arrow placement="top">
          <span className="flex shrink-0 items-center text-gray-70">
            <Icon icon="MdInfoOutline" className="text-sm" />
          </span>
        </Tooltip>
      )}

      <span className="h-px flex-1 bg-neutral-30" />

      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export default ReportBand;
